<script>
import { computed, ref, provide, watch } from 'vue';
// composable
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useLabelSuggestions } from 'dashboard/composables/useLabelSuggestions';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageSelection } from 'dashboard/composables/useMessageSelection';
import { useMapGetter } from 'dashboard/composables/store';

// components
import ReplyBox from './ReplyBox.vue';
import MessageList from 'next/message/MessageList.vue';
import ConversationLabelSuggestion from './conversation/LabelSuggestion.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ForwardMessageDialog from 'dashboard/modules/conversations/components/ForwardMessageDialog.vue';

// stores and apis
import { mapGetters } from 'vuex';
import ConversationApi from 'dashboard/api/inbox/conversation';

// mixins
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';

// utils
import { emitter } from 'shared/helpers/mitt';
import { getTypingUsersText } from '../../../helper/commons';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  filterDuplicateSourceMessages,
  getReadMessages,
  getUnreadMessages,
} from 'dashboard/helper/conversationHelper';

// constants
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { INBOX_TYPES, isAWhatsAppChannel } from 'dashboard/helper/inbox';

export default {
  components: {
    MessageList,
    ReplyBox,
    Banner,
    ConversationLabelSuggestion,
    Spinner,
    NextButton,
    ForwardMessageDialog,
  },
  mixins: [inboxMixin],
  setup() {
    const isPopOutReplyBox = ref(false);
    const conversationPanelRef = ref(null);
    const groupParticipants = ref([]);
    const currentChat = useMapGetter('getSelectedChat');
    const getInbox = useMapGetter('inboxes/getInbox');
    const isWhatsAppGroupConversation = computed(() => {
      const chat = currentChat.value;
      const inbox = getInbox.value(chat?.inbox_id);
      return (
        isAWhatsAppChannel(inbox) &&
        String(chat?.meta?.sender?.identifier || '').endsWith('@g.us')
      );
    });
    const groupRosterKey = computed(() =>
      isWhatsAppGroupConversation.value ? currentChat.value?.id : null
    );

    watch(
      groupRosterKey,
      async conversationId => {
        groupParticipants.value = [];
        if (!conversationId) return;

        try {
          const { data } =
            await ConversationApi.getGroupParticipants(conversationId);
          if (groupRosterKey.value === conversationId) {
            groupParticipants.value = data.participants || [];
          }
        } catch {
          if (groupRosterKey.value === conversationId) {
            groupParticipants.value = [];
          }
        }
      },
      { immediate: true }
    );

    const keyboardEvents = {
      Escape: {
        action: () => {
          isPopOutReplyBox.value = false;
        },
      },
    };

    useKeyboardEvents(keyboardEvents);

    const {
      captainTasksEnabled,
      isLabelSuggestionFeatureEnabled,
      getLabelSuggestions,
    } = useLabelSuggestions();

    provide('contextMenuElementTarget', conversationPanelRef);

    const {
      isSelectMode,
      selectedIds,
      exitSelectMode,
      deselect,
      resetForConversation,
    } = useMessageSelection();

    return {
      isPopOutReplyBox,
      captainTasksEnabled,
      getLabelSuggestions,
      isLabelSuggestionFeatureEnabled,
      conversationPanelRef,
      groupParticipants,
      isWhatsAppGroupConversation,
      isSelectMode,
      selectedIds,
      exitSelectMode,
      deselect,
      resetForConversation,
    };
  },
  data() {
    return {
      isLoadingPrevious: true,
      heightBeforeLoad: null,
      conversationPanel: null,
      hasUserScrolled: false,
      isProgrammaticScroll: false,
      messageSentSinceOpened: false,
      labelSuggestions: [],
      showMultiForward: false,
    };
  },

  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUserId: 'getCurrentUserID',
      listLoadingStatus: 'getAllMessagesLoaded',
      currentAccountId: 'getCurrentAccountId',
    }),
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    shouldShowLabelSuggestions() {
      return (
        this.isOpen &&
        this.captainTasksEnabled &&
        this.isLabelSuggestionFeatureEnabled &&
        !this.messageSentSinceOpened
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList;
      return userList.length !== 0;
    },
    typingUserNames() {
      const userList = this.typingUsersList;
      if (this.isAnyoneTyping) {
        const [i18nKey, params] = getTypingUsersText(userList);
        return this.$t(i18nKey, params);
      }

      return '';
    },
    getMessages() {
      const messages = this.currentChat.messages || [];
      if (this.isAWhatsAppChannel) {
        return filterDuplicateSourceMessages(messages);
      }
      return messages;
    },
    // Live-derived from the current conversation's messages (not a stale
    // snapshot), ordered chronologically, so a message forwards its CURRENT
    // content. Deleted/customer-revoked messages are excluded: Chatwoot retains
    // their content for evidence but it must never be re-sent to a third party.
    selectedMessages() {
      const ids = this.selectedIds;
      return (this.currentChat.messages || [])
        .filter(message => {
          if (!ids.has(message.id)) return false;
          const attrs = message.content_attributes || {};
          return !attrs.deleted && !attrs.deleted_by_contact;
        })
        .sort(
          (a, b) => (a.created_at || 0) - (b.created_at || 0) || a.id - b.id
        );
    },
    readMessages() {
      return getReadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    unReadMessages() {
      return getUnreadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    shouldShowSpinner() {
      return (
        (this.currentChat && this.currentChat.dataFetched === undefined) ||
        (!this.listLoadingStatus && this.isLoadingPrevious)
      );
    },
    // Check there is a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const { additional_attributes: additionalAttributes = {} } = this.inbox;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return (
        this.inbox.channel_type === INBOX_TYPES.FB &&
        additionalAttributes.type === 'instagram_direct_message' &&
        instagramInbox
      );
    },

    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
            agent_reply_time_window: agentReplyTimeWindow,
          } = additionalAttributes;
          return (
            agentReplyTimeWindowMessage ||
            this.$t('CONVERSATION.API_HOURS_WINDOW', {
              hours: agentReplyTimeWindow,
            })
          );
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (this.isAFacebookInbox || this.isAnInstagramChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (this.isAWhatsAppCloudChannel) {
        return REPLY_POLICY.WHATSAPP_CLOUD;
      }
      if (this.isATiktokChannel) {
        return REPLY_POLICY.TIKTOK;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAnInstagramChannel
      ) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (this.isATiktokChannel) {
        return this.$t('CONVERSATION.48_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    unreadMessageCount() {
      return this.currentChat.unread_count || 0;
    },
    unreadMessageLabel() {
      const count =
        this.unreadMessageCount > 9 ? '9+' : this.unreadMessageCount;
      const label =
        this.unreadMessageCount > 1
          ? 'CONVERSATION.UNREAD_MESSAGES'
          : 'CONVERSATION.UNREAD_MESSAGE';
      return `${count} ${this.$t(label)}`;
    },
    inboxSupportsReplyTo() {
      const incoming = this.inboxHasFeature(INBOX_FEATURES.REPLY_TO);
      const outgoing =
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO_OUTGOING) &&
        !this.is360DialogWhatsAppChannel;

      return { incoming, outgoing };
    },
  },

  watch: {
    currentChat(newChat, oldChat) {
      if (newChat.id === oldChat.id) {
        return;
      }
      // Drop any multi-select that belonged to the previous conversation.
      this.resetForConversation(newChat?.id);
      this.showMultiForward = false;
      this.fetchAllAttachmentsFromCurrentChat();
      this.fetchSuggestions();
      this.messageSentSinceOpened = false;
    },
  },

  created() {
    emitter.on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    // when a message is sent we set the flag to true this hides the label suggestions,
    // until the chat is changed and the flag is reset in the watch for currentChat
    emitter.on(BUS_EVENTS.MESSAGE_SENT, () => {
      this.messageSentSinceOpened = true;
    });
  },

  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
    this.fetchSuggestions();
  },

  unmounted() {
    this.removeBusListeners();
    this.removeScrollListener();
    // The selection lives in a module-level singleton; clear it when leaving the
    // conversation view so it can't leak into a later-opened conversation (the
    // currentChat watcher misses an unmount → remount onto the same id).
    this.exitSelectMode();
  },

  methods: {
    onCancelSelect() {
      this.exitSelectMode();
    },
    onForwardSelected() {
      this.showMultiForward = true;
    },
    // Only the messages that actually went out are removed from the selection;
    // any that failed stay ticked so the dialog (kept open) can retry just them.
    onForwarded(sentIds) {
      this.deselect(sentIds);
      // Close + exit once nothing forwardable remains selected (any leftover
      // deleted/revoked ids are cleared by exitSelectMode).
      if (!this.selectedMessages.length) {
        this.showMultiForward = false;
        this.exitSelectMode();
      }
    },
    closeMultiForward() {
      this.showMultiForward = false;
    },
    async fetchSuggestions() {
      // start empty, this ensures that the label suggestions are not shown
      this.labelSuggestions = [];

      if (this.isLabelSuggestionDismissed()) {
        return;
      }

      // Early exit if conversation already has labels - no need to suggest more
      const existingLabels = this.currentChat?.labels || [];
      if (existingLabels.length > 0) return;

      if (!this.captainTasksEnabled || !this.isLabelSuggestionFeatureEnabled) {
        return;
      }

      this.labelSuggestions = await this.getLabelSuggestions();

      // once the labels are fetched, we need to scroll to bottom
      // but we need to wait for the DOM to be updated
      // so we use the nextTick method
      this.$nextTick(() => {
        // this param is added to route, telling the UI to navigate to the message
        // it is triggered by the SCROLL_TO_MESSAGE method
        // see setActiveChat on ConversationView.vue for more info
        const { messageId } = this.$route.query;

        // only trigger the scroll to bottom if the user has not scrolled
        // and there's no active messageId that is selected in view
        if (!messageId && !this.hasUserScrolled) {
          this.scrollToBottom();
        }
      });
    },
    isLabelSuggestionDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.currentChat.id
      );
    },
    fetchAllAttachmentsFromCurrentChat() {
      this.$store.dispatch('fetchAllAttachments', this.currentChat.id);
    },
    removeBusListeners() {
      emitter.off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    },
    onScrollToMessage({ messageId = '' } = {}) {
      this.$nextTick(() => {
        const messageElement = document.getElementById('message' + messageId);
        if (messageElement) {
          this.isProgrammaticScroll = true;
          messageElement.scrollIntoView({ behavior: 'smooth' });
          this.flashJumpTarget(messageElement);
          this.fetchPreviousMessages();
          this.keepJumpTargetInView(messageId);
        } else {
          this.scrollToBottom();
        }
      });
      this.makeMessagesRead();
    },
    // Media chips and the ranged gap-fill render AFTER the first scrollIntoView,
    // shifting layout by hundreds of px in media-heavy threads (observed ~880px
    // drift on a large WhatsApp group deep link) — re-assert the target's
    // position until layout settles. Cold direct-URL loads fetch every image
    // fresh, so the window runs to 6s — but any real user interaction (wheel,
    // touch, keys) cancels the remaining re-asserts so we never yank someone
    // who has started reading elsewhere.
    keepJumpTargetInView(messageId) {
      let userMoved = false;
      const markMoved = () => {
        userMoved = true;
      };
      const opts = { once: true, passive: true };
      window.addEventListener('wheel', markMoved, opts);
      window.addEventListener('touchstart', markMoved, opts);
      window.addEventListener('keydown', markMoved, opts);
      [400, 1000, 1800, 3200, 6000].forEach(delay => {
        setTimeout(() => {
          if (userMoved) return;
          const el = document.getElementById('message' + messageId);
          const panel = this.conversationPanel;
          if (!el || !panel) return;
          const elTop = el.getBoundingClientRect().top;
          const panelRect = panel.getBoundingClientRect();
          const drifted =
            elTop < panelRect.top - 4 || elTop > panelRect.bottom - 40;
          if (drifted) {
            this.isProgrammaticScroll = true;
            el.scrollIntoView({ block: 'start' });
          }
        }, delay);
      });
    },
    // Briefly tint the jump target (search hit / reply-quote) so the operator
    // can tell WHICH message they landed on once the smooth scroll settles.
    flashJumpTarget(el) {
      el.classList.remove('mh-jump-flash');
      // force a reflow so re-clicking the same hit restarts the animation
      el.getBoundingClientRect();
      el.classList.add('mh-jump-flash');
      setTimeout(() => el.classList.remove('mh-jump-flash'), 2400);
    },
    addScrollListener() {
      this.conversationPanel = this.$el.querySelector('.conversation-panel');
      this.setScrollParams();
      this.conversationPanel.addEventListener('scroll', this.handleScroll);
      this.$nextTick(() => this.scrollToBottom());
      this.isLoadingPrevious = false;
    },
    removeScrollListener() {
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
    },
    scrollToBottom() {
      this.isProgrammaticScroll = true;
      const panel = this.conversationPanel;
      if (!panel) return;

      // The components-next Message renders `#message{id}` ids, not the legacy
      // .message--read/.message--unread classes the old calculation relied on —
      // with those gone, the previous math left the panel stuck mid-thread on
      // chat switch. Scroll to the first unread message if there is one (kept a
      // little below the top for context), otherwise to the true bottom so the
      // latest message is always in view when opening a conversation.
      const firstUnreadId = this.unReadMessages?.[0]?.id;
      const unreadEl = firstUnreadId
        ? panel.querySelector(`#message${firstUnreadId}`)
        : null;

      if (unreadEl) {
        panel.scrollTop = Math.max(0, unreadEl.offsetTop - 80);
      } else {
        panel.scrollTop = panel.scrollHeight;
      }
    },
    setScrollParams() {
      this.heightBeforeLoad = this.conversationPanel.scrollHeight;
      this.scrollTopBeforeLoad = this.conversationPanel.scrollTop;
    },

    async fetchPreviousMessages(scrollTop = 0) {
      this.setScrollParams();
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before: this.currentChat.messages[0].id,
          });
          const heightDifference =
            this.conversationPanel.scrollHeight - this.heightBeforeLoad;
          this.conversationPanel.scrollTop =
            this.scrollTopBeforeLoad + heightDifference;
          this.setScrollParams();
        } catch (error) {
          // Ignore Error
        } finally {
          this.isLoadingPrevious = false;
        }
      }
    },

    handleScroll(e) {
      if (this.isProgrammaticScroll) {
        // Reset the flag
        this.isProgrammaticScroll = false;
        this.hasUserScrolled = false;
      } else {
        this.hasUserScrolled = true;
      }
      emitter.emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
      this.fetchPreviousMessages(e.target.scrollTop);
    },

    makeMessagesRead() {
      this.$store.dispatch('markMessagesRead', { id: this.currentChat.id });
    },
    async handleMessageRetry(message) {
      if (!message) return;
      const payload = useSnakeCase(message);
      await this.$store.dispatch('sendMessageWithData', payload);
    },
  },
};
</script>

<template>
  <div class="flex flex-col justify-between flex-grow h-full min-w-0 m-0">
    <Banner
      v-if="!currentChat.can_reply"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="replyWindowBannerMessage"
      :href-link="replyWindowLink"
      :href-link-text="replyWindowLinkText"
    />
    <Banner
      v-else-if="hasDuplicateInstagramInbox"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="$t('CONVERSATION.OLD_INSTAGRAM_INBOX_REPLY_BANNER')"
    />
    <MessageList
      ref="conversationPanelRef"
      class="conversation-panel flex-shrink flex-grow basis-px flex flex-col overflow-y-auto relative h-full m-0 pb-4"
      :current-user-id="currentUserId"
      :first-unread-id="unReadMessages[0]?.id"
      :is-an-email-channel="isAnEmailChannel"
      :inbox-supports-reply-to="inboxSupportsReplyTo"
      :messages="getMessages"
      :group-participants="groupParticipants"
      :is-whats-app-group-conversation="isWhatsAppGroupConversation"
      @retry="handleMessageRetry"
    >
      <template #beforeAll>
        <transition name="slide-up">
          <!-- eslint-disable-next-line vue/require-toggle-inside-transition -->
          <li
            class="min-h-[4rem] flex flex-shrink-0 flex-grow-0 items-center flex-auto justify-center max-w-full mt-0 mr-0 mb-1 ml-0 relative first:mt-auto last:mb-0"
          >
            <Spinner v-if="shouldShowSpinner" class="text-n-brand" />
          </li>
        </transition>
      </template>
      <template #unreadBadge>
        <li
          v-show="unreadMessageCount != 0"
          class="list-none flex justify-center items-center"
        >
          <span
            class="shadow-lg rounded-full bg-n-brand text-white text-xs font-medium my-2.5 mx-auto px-2.5 py-1.5"
          >
            {{ unreadMessageLabel }}
          </span>
        </li>
      </template>
      <template #after>
        <ConversationLabelSuggestion
          v-if="shouldShowLabelSuggestions"
          :suggested-labels="labelSuggestions"
          :chat-labels="currentChat.labels"
          :conversation-id="currentChat.id"
        />
      </template>
    </MessageList>
    <div
      v-if="isSelectMode"
      class="flex items-center justify-between gap-2 px-3 py-2 border-t bg-n-solid-1 border-n-weak"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{
          $t('CONVERSATION.CONTEXT_MENU.FORWARD.SELECT_MODE_COUNT', {
            count: selectedMessages.length,
          })
        }}
      </span>
      <div class="flex items-center gap-2">
        <NextButton
          faded
          slate
          sm
          :label="$t('CONVERSATION.CONTEXT_MENU.FORWARD.SELECT_MODE_CANCEL')"
          @click="onCancelSelect"
        />
        <NextButton
          blue
          sm
          icon="i-lucide-forward"
          :disabled="!selectedMessages.length"
          :label="$t('CONVERSATION.CONTEXT_MENU.FORWARD.SELECT_MODE_FORWARD')"
          @click="onForwardSelected"
        />
      </div>
    </div>
    <woot-modal
      v-if="showMultiForward"
      v-model:show="showMultiForward"
      :on-close="closeMultiForward"
    >
      <ForwardMessageDialog
        :messages="selectedMessages"
        @close="closeMultiForward"
        @forwarded="onForwarded"
      />
    </woot-modal>
    <div
      class="flex relative flex-col"
      :class="{
        'modal-mask': isPopOutReplyBox,
        'bg-n-surface-1': !isPopOutReplyBox,
      }"
    >
      <div
        v-if="isAnyoneTyping"
        class="absolute flex items-center w-full h-0 -top-7"
      >
        <div
          class="flex py-2 pr-4 pl-5 shadow-md rounded-full bg-white dark:bg-n-solid-3 text-n-slate-11 text-xs font-semibold my-2.5 mx-auto"
        >
          {{ typingUserNames }}
          <img
            class="w-6 ltr:ml-2 rtl:mr-2"
            src="assets/images/typing.gif"
            alt="Someone is typing"
          />
        </div>
      </div>
      <ReplyBox
        :pop-out-reply-box="isPopOutReplyBox"
        :group-participants="groupParticipants"
        @update:pop-out-reply-box="isPopOutReplyBox = $event"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.modal-mask {
  @apply fixed;

  &::v-deep {
    .ProseMirror-woot-style {
      @apply max-h-[25rem];
    }

    .reply-box {
      @apply border border-n-weak max-w-[75rem] w-[70%];

      &.is-private {
        @apply dark:border-n-amber-3/30 border-n-amber-12/5;
      }
    }

    .reply-box .reply-box__top {
      @apply relative min-h-[27.5rem];
    }

    .reply-box__top .input {
      @apply min-h-[27.5rem];
    }

    .emoji-dialog {
      @apply absolute ltr:left-auto rtl:right-auto bottom-1;
    }
  }
}

// Search-jump / reply-jump landing flash. Applied via classList by
// flashJumpTarget() onto the #message<id> row (rendered by a child component,
// hence :deep). Radix --amber-5 tracks light/dark themes by itself.
:deep(.mh-jump-flash) {
  animation: mh-jump-flash 2.4s ease-out 1;
  @apply rounded-lg;
}

@keyframes mh-jump-flash {
  0%,
  30% {
    background-color: rgb(var(--amber-5));
  }
  100% {
    background-color: transparent;
  }
}
</style>
